import type { LessonPart } from '../hooks/useLessonApi';
import type { Message } from '../hooks/useLessonState';
import { buildPartStrip, type RunMode } from './viewModel';

export interface RailPhrase {
  id: string;
  targetText: string | null;
  /** Two-to-five-word meaning (#341); null for a step that has none. The narration is never shown here (#340). */
  gloss: string | null;
  audioUrl: string;
}

export interface RailView {
  parts: Array<{
    id: string;
    label: string;
    state: 'done' | 'current' | 'todo';
    phrases: RailPhrase[];
  }>;
  /** Phrases that belong to no part (practice). */
  loose: RailPhrase[];
  /** Nothing worth a rail yet. */
  isEmpty: boolean;
}

/** A phrase learned before the lesson was resumed (#347): it has no messages on screen, only the stored record. */
export interface EarlierPhrase {
  partId: string | null;
  phrase: RailPhrase;
}

interface Input {
  mode: RunMode;
  messages: Message[];
  parts: LessonPart[];
  activePartId: string | null;
  /** Phrases learned in an earlier sitting of this run; they come before those learned on screen. */
  earlier?: EarlierPhrase[];
}

const isModelPhrase = (m: Message): boolean =>
  m.sender === 'tutor' &&
  m.stepKind === 'play_audio' &&
  !m.persona &&
  !m.audioOnly &&
  !!m.audioUrls?.natural;

/**
 * The rail beside the card on a wide screen (#307): the parts, and under them the phrases the learner
 * has learned so far, each replayable (BR-009): the target phrase, its short gloss and a play button. A phrase counts as learned once an attempt at it was
 * accepted. Check-ins show no phrase text (BR-043, BR-048), so their rail is parts only.
 */
export function buildRail({
  mode,
  messages,
  parts,
  activePartId,
  earlier = [],
}: Input): RailView {
  const learned: EarlierPhrase[] = [];
  if (mode !== 'checkin') {
    learned.push(...earlier);
    messages.forEach((m, i) => {
      if (!isModelPhrase(m)) return;
      // Learned only if an accepted attempt follows before the next phrase.
      const next = messages.findIndex(
        (n, j) => j > i && n.stepKind === 'play_audio' && isModelPhrase(n)
      );
      const end = next === -1 ? messages.length : next;
      const accepted = messages
        .slice(i + 1, end)
        .some((n) => n.stepKind === 'give_feedback' && n.success !== false);
      if (!accepted) return;
      learned.push({
        partId: m.partId ?? null,
        phrase: {
          id: m.id,
          targetText: m.targetText ?? null,
          gloss: m.gloss ?? null,
          audioUrl: m.audioUrls!.natural,
        },
      });
    });
  }
  const strip = buildPartStrip(parts, activePartId);
  const known = new Set(parts.map((p) => p.id));
  const view = strip.parts.map((p) => ({
    ...p,
    phrases: learned.filter((l) => l.partId === p.id).map((l) => l.phrase),
  }));
  const loose = learned
    .filter((l) => !l.partId || !known.has(l.partId))
    .map((l) => l.phrase);
  return {
    parts: view,
    loose,
    isEmpty: view.length === 0 && loose.length === 0,
  };
}
